import { useI18n } from '../../i18n';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { PageHeader, PageContainer, ExportButton, showToast, ImageUploadField } from '../../components/shared';
import {
  useSuppliers,
  useSuppliersStats,
  useSupplier,
  useCreateSupplier,
  useUpdateSupplier,
  useExport,
  useDebounce,
  useStores,
} from '../../hooks';
import {
  SupplierKPICards,
  SupplierTable,
  type SupplierDisplay,
  type StatusTab,
} from './components';

type SupplierStatus = SupplierDisplay['status'];
type SupplierModalMode = 'view' | 'create' | 'edit' | 'delete-confirm' | null;

type SupplierApiItem = {
  id: string;
  code?: string | null;
  name: string;
  contact_name?: string | null;
  phone?: string | null;
  contact_phone?: string | null;
  email?: string | null;
  contact_email?: string | null;
  address?: string | null;
  payment_method?: string | null;
  payment_terms?: string | null;
  lead_time_days?: number | null;
  status?: string | null;
  is_active?: boolean;
};

type SupplierListEnvelope = {
  results?: SupplierApiItem[];
  items?: SupplierApiItem[];
  data?: SupplierApiItem[] | { results?: SupplierApiItem[]; items?: SupplierApiItem[] };
  count?: number;
  total?: number;
  meta?: { count?: number };
};

type SupplierFormData = {
  name: string;
  tax_code: string;
  contact_name: string;
  contact_phone: string;
  email: string;
  lead_time_days: string;
  metadata: string;
  // Bug #159: filter Status có Active/Inactive nhưng form không có trường này —
  // thêm để admin set được trạng thái (map sang is_active của API).
  status: 'active' | 'inactive';
  /** Logo/ảnh NCC — BE bật image_urls 03/09/2026 (sheet 2 mục #14). */
  imageUrls: string[];
};

type SupplierFormErrors = Partial<Record<keyof SupplierFormData, string>>;
type SupplierFormTouched = Partial<Record<keyof SupplierFormData, boolean>>;

const emptyForm: SupplierFormData = {
  name: '',
  tax_code: '',
  contact_name: '',
  contact_phone: '',
  email: '',
  lead_time_days: '',
  metadata: '',
  status: 'active',
  imageUrls: [],
};

const formFieldOrder: Array<keyof SupplierFormData> = [
  'name',
  'tax_code',
  'contact_name',
  'contact_phone',
  'email',
  'lead_time_days',
  'metadata',
];

function normalizeSupplierStatus(supplier: SupplierApiItem): SupplierStatus {
  const rawStatus = (supplier.status ?? '').toString().trim().toLowerCase();
  if (rawStatus === 'active') return 'active';
  if (rawStatus === 'inactive') return 'inactive';
  if (rawStatus === 'pending') return 'pending';
  if (supplier.is_active === true) return 'active';
  return 'inactive';
}

function extractSupplierList(payload: unknown): SupplierApiItem[] {
  if (Array.isArray(payload)) return payload as SupplierApiItem[];
  const data = payload as SupplierListEnvelope | undefined;
  if (!data) return [];
  if (Array.isArray(data.data)) return data.data;
  if (data.data && typeof data.data === 'object') {
    return data.data.results ?? data.data.items ?? [];
  }
  return data.results ?? data.items ?? [];
}

function extractSupplierCount(payload: unknown, fallbackCount: number): number {
  if (Array.isArray(payload)) return payload.length;
  const data = payload as SupplierListEnvelope | undefined;
  return data?.count ?? data?.total ?? data?.meta?.count ?? fallbackCount;
}

/**
 * Số dòng lấy về khi người dùng đang tìm kiếm — đủ cho danh sách nhà cung cấp
 * thực tế của một chuỗi cửa hàng. Vượt mốc này thì phải nhờ BE mở rộng
 * `search_fields`, xem ghi chú ở chỗ gọi `useSuppliers`.
 */
const SEARCH_FETCH_SIZE = 500;

/** Bỏ mọi ký tự không phải chữ số: `+84 901 234 567` và `0901234567` phải khớp nhau. */
function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

function toSupplierDisplay(supplier: SupplierApiItem): SupplierDisplay {
  return {
    id: supplier.id,
    code: supplier.code ?? '-',
    name: supplier.name,
    contactName: supplier.contact_name ?? undefined,
    phone: supplier.contact_phone ?? supplier.phone ?? undefined,
    email: supplier.contact_email ?? supplier.email ?? undefined,
    taxCode: (supplier as any).tax_code ?? undefined,
    address: supplier.address ?? undefined,
    paymentMethod: supplier.payment_method ?? undefined,
    paymentTerms: supplier.payment_terms ?? undefined,
    deliveryTime:
      typeof supplier.lead_time_days === 'number' ? `${supplier.lead_time_days} days` : undefined,
    status: normalizeSupplierStatus(supplier),
  };
}

function toFormData(supplier?: SupplierApiItem): SupplierFormData {
  if (!supplier) return emptyForm;
  return {
    name: supplier.name ?? '',
    tax_code: (supplier as any).tax_code ?? '',
    contact_name: supplier.contact_name ?? '',
    contact_phone: supplier.contact_phone ?? supplier.phone ?? '',
    email: supplier.contact_email ?? supplier.email ?? '',
    lead_time_days:
      typeof supplier.lead_time_days === 'number' ? String(supplier.lead_time_days) : '',
    metadata: typeof (supplier as any).metadata === 'string' ? (supplier as any).metadata : '',
    status: normalizeSupplierStatus(supplier) === 'inactive' ? 'inactive' : 'active',
    imageUrls: Array.isArray((supplier as any).image_urls)
      ? ((supplier as any).image_urls as string[])
      : [],
  };
}

/** Bug #209: thông báo validation phải đi qua i18n — nhận `t` thay vì chuỗi cứng. */
function validateField(
  field: keyof SupplierFormData,
  form: SupplierFormData,
  t: (key: string) => string
): string | undefined {
  if (field === 'name' && !form.name.trim()) {
    return t('validationSupplierNameRequired');
  }
  if (field === 'email' && form.email.trim()) {
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!isValid) return t('validationInvalidEmailFormat');
  }
  if (field === 'contact_phone' && form.contact_phone.trim()) {
    const isValid = /^[+0-9\s()-]{8,20}$/.test(form.contact_phone.trim());
    if (!isValid) return t('validationInvalidPhoneFormat');
  }
  if (field === 'lead_time_days' && form.lead_time_days.trim()) {
    const value = Number(form.lead_time_days);
    if (!Number.isInteger(value) || value < 0) {
      return t('validationLeadTimeNonNegativeInteger');
    }
  }
  return undefined;
}

function validateForm(form: SupplierFormData, t: (key: string) => string): SupplierFormErrors {
  const errors: SupplierFormErrors = {};
  for (const field of formFieldOrder) {
    const message = validateField(field, form, t);
    if (message) errors[field] = message;
  }
  return errors;
}

export function SuppliersPage() {
  const { t } = useI18n();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 500);
  const [activeTab, setActiveTab] = useState<StatusTab>('all');
  /**
   * Lọc theo cửa hàng (mục #18). Rỗng = không lọc.
   *
   * PM chốt 05/09: GIỮ bộ lọc, chỉ gọi đúng tên — "NCC đã từng nhập cho cửa
   * hàng". Cửa hàng mới trả rỗng và đó là kết quả đúng, nên trạng thái rỗng
   * phải có câu giải thích riêng chứ không dùng chữ "không tìm thấy" chung.
   */
  const [storeFilter, setStoreFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState<SupplierModalMode>(null);
  const [activeSupplierId, setActiveSupplierId] = useState<string | null>(null);
  const [formData, setFormData] = useState<SupplierFormData>(emptyForm);
  const [formErrors, setFormErrors] = useState<SupplierFormErrors>({});
  const [formTouched, setFormTouched] = useState<SupplierFormTouched>({});
  const fieldRefs = useRef<
    Partial<Record<keyof SupplierFormData, HTMLInputElement | HTMLSelectElement | null>>
  >({});
  const pageSize = 10;

  // Bug #187: BE chỉ tìm theo `name` và `tax_code` (contract API), trong khi ô
  // tìm kiếm ghi "Search by name, code or phone". Gõ số điện thoại xuống server
  // là chắc chắn trả rỗng — và rỗng thì client cũng không còn gì để lọc.
  //
  // Khi ĐANG tìm kiếm: không gửi `search` nữa, lấy trọn danh sách theo tab rồi
  // lọc ở client trên đủ các trường mà ô tìm kiếm hứa. Khi KHÔNG tìm kiếm: giữ
  // nguyên phân trang server như cũ. Cùng cách PromotionsPage đang làm.
  const isSearching = debouncedSearch.trim().length > 0;

  const { data: suppliersData, isLoading, error } = useSuppliers({
    page: isSearching ? 1 : currentPage,
    page_size: isSearching ? SEARCH_FETCH_SIZE : pageSize,
    is_active: activeTab === 'all' ? undefined : activeTab === 'active',
    // Mục #18: lọc "NCC đã TỪNG NHẬP HÀNG cho cửa hàng này" — không phải "NCC
    // thuộc cửa hàng". Xem ghi chú ở `SupplierListParams.store_id`.
    store_id: storeFilter || undefined,
  });
  const { stats: supplierStats, isLoading: statsLoading } = useSuppliersStats();

  const { data: storesData } = useStores({ page_size: 100 });
  const storeOptions = useMemo(
    () =>
      ((storesData as { results?: Array<{ id: string; name: string }> } | undefined)?.results ?? [])
        .map((s) => ({ value: s.id, label: s.name })),
    [storesData]
  );

  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();
  // useDeleteSupplier không còn được trang này dùng: nút thùng rác đã map sang
  // deactivate (xem handleConfirmDelete). Hook giữ nguyên trong useSuppliers.ts
  // cho màn Deleted/Restore nếu sau này PM duyệt.

  const { data: supplierDetailData, isLoading: isLoadingDetail } = useSupplier(activeSupplierId ?? '', {
    enabled: !!activeSupplierId && (modalMode === 'view' || modalMode === 'edit'),
  });

  const supplierList = useMemo(() => extractSupplierList(suppliersData), [suppliersData]);
  const suppliersCount = useMemo(
    () => extractSupplierCount(suppliersData, supplierList.length),
    [suppliersData, supplierList.length]
  );
  const suppliers = useMemo(() => supplierList.map(toSupplierDisplay), [supplierList]);

  // Bug #187: lọc client trên đúng những trường ô tìm kiếm hứa — tên, mã, mã số
  // thuế, email và SỐ ĐIỆN THOẠI. Số điện thoại so theo chữ số thuần vì cả lúc
  // nhập lẫn lúc lưu đều không chuẩn hoá: `+84 901 234 567` phải tìm được bằng
  // `0901234567` hay `901234`.
  const matchedSuppliers = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return suppliers;

    const queryDigits = digitsOnly(query);
    return suppliers.filter((supplier) => {
      // Loại giá trị placeholder '-' khỏi tập so khớp: `code` không tồn tại
      // trong contract supplier nên mọi dòng đều là '-' — không loại thì gõ
      // đúng một dấu gạch là khớp TOÀN BỘ danh sách (reviewer bắt).
      const haystack = [supplier.name, supplier.code, supplier.taxCode, supplier.email, supplier.contactName];
      if (haystack.some((field) => field && field !== '-' && field.toLowerCase().includes(query))) {
        return true;
      }
      // Cần ít nhất 3 chữ số để một chuỗi mới được coi là đang tìm theo SĐT —
      // gõ "1" mà khớp mọi số điện thoại có chữ số 1 thì bộ lọc thành vô dụng.
      if (queryDigits.length >= 3 && supplier.phone) {
        return digitsOnly(supplier.phone).includes(queryDigits);
      }
      return false;
    });
  }, [suppliers, debouncedSearch]);

  // Đang tìm kiếm thì phân trang trên tập ĐÃ LỌC, không tìm thì dùng count của
  // server như cũ — số ở footer luôn khớp số dòng duyệt được.
  const visibleSuppliers = useMemo(
    () =>
      isSearching
        ? matchedSuppliers.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : matchedSuppliers,
    [isSearching, matchedSuppliers, currentPage, pageSize]
  );
  const visibleCount = isSearching ? matchedSuppliers.length : suppliersCount;

  // Đổi từ khoá hoặc đổi tab làm tập kết quả co lại — quay về trang 1, tránh
  // đứng ở một trang đã không còn tồn tại và thấy bảng trống.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, activeTab]);

  const stats = useMemo(
    () => ({
      totalSuppliers: supplierStats.total,
      activeContracts: supplierStats.active,
      avgDeliveryDays: supplierStats.avgDeliveryDays,
      pendingPayments: supplierStats.pendingPayment,
    }),
    [supplierStats]
  );

  const activeSupplierFromList = useMemo(
    () => supplierList.find((supplier) => supplier.id === activeSupplierId) ?? null,
    [supplierList, activeSupplierId]
  );

  const supplierDetail = useMemo<SupplierApiItem | null>(() => {
    const raw = supplierDetailData as unknown;
    if (!raw || typeof raw !== 'object') return null;

    const maybeEnvelope = raw as { data?: unknown };
    if (maybeEnvelope.data && typeof maybeEnvelope.data === 'object') {
      return maybeEnvelope.data as SupplierApiItem;
    }

    return raw as SupplierApiItem;
  }, [supplierDetailData]);

  const isSubmitting = createSupplier.isPending || updateSupplier.isPending;

  const closeModal = useCallback(() => {
    setModalMode(null);
    setActiveSupplierId(null);
    setFormData(emptyForm);
    setFormErrors({});
    setFormTouched({});
      }, []);

  useEffect(() => {
    if (!modalMode) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [modalMode]);

  useEffect(() => {
    if (modalMode === 'create') {
      setFormData(emptyForm);
      return;
    }

    if ((modalMode === 'view' || modalMode === 'edit') && activeSupplierId) {
      const source = supplierDetail ?? activeSupplierFromList;
      if (source) {
        setFormData(toFormData(source));
      }
    }
  }, [modalMode, activeSupplierId, supplierDetail, activeSupplierFromList]);

  const openModal = useCallback(
    (id: string, mode: Exclude<SupplierModalMode, null | 'create'>) => {
      setActiveSupplierId(id);
      setModalMode(mode);
      setFormErrors({});
      setFormTouched({});
          },
    []
  );

  const handleAddSupplier = useCallback(() => {
    setModalMode('create');
    setActiveSupplierId(null);
    setFormData(emptyForm);
    setFormErrors({});
    setFormTouched({});
      }, []);

  const handleFieldChange = useCallback(
    (field: keyof SupplierFormData, value: string) => {
      setFormData((prev) => {
        const next = { ...prev, [field]: value } as SupplierFormData;
        if (formTouched[field]) {
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            [field]: validateField(field, next, t),
          }));
        }
        return next;
      });
    },
    [formTouched, t]
  );

  const handleFieldBlur = useCallback(
    (field: keyof SupplierFormData) => {
      setFormTouched((prev) => ({ ...prev, [field]: true }));
      setFormErrors((prev) => ({
        ...prev,
        [field]: validateField(field, formData, t),
      }));
    },
    [formData, t]
  );

  const handleSubmitForm = useCallback(async () => {
    const errors = validateForm(formData, t);
    setFormErrors(errors);
    setFormTouched(
      formFieldOrder.reduce<SupplierFormTouched>((acc, field) => ({ ...acc, [field]: true }), {})
    );

    const firstInvalid = formFieldOrder.find((field) => errors[field]);
    if (firstInvalid) {
      fieldRefs.current[firstInvalid]?.focus();
      return;
    }

    const payload = {
      name: formData.name.trim(),
      tax_code: formData.tax_code.trim() || undefined,
      contact_name: formData.contact_name.trim() || undefined,
      contact_phone: formData.contact_phone.trim() || undefined,
      email: formData.email.trim() || undefined,
      lead_time_days: formData.lead_time_days.trim() ? Number(formData.lead_time_days) : undefined,
      metadata: formData.metadata.trim() || undefined,
      // Bug #159: gửi trạng thái xuống API để filter Active/Inactive khớp với dữ liệu.
      is_active: formData.status === 'active',
      // Tạo mới: không có ảnh thì bỏ field, để BE dùng mặc định thay vì ép
      // mảng rỗng. Sửa: gửi nguyên mảng — rỗng ở đây LÀ ý định xoá hết ảnh.
      image_urls:
        modalMode === 'create' && formData.imageUrls.length === 0
          ? undefined
          : formData.imageUrls,
    };

    try {
      if (modalMode === 'create') {
        await createSupplier.mutateAsync(payload);
        closeModal();
        return;
      }

      if (modalMode === 'edit' && activeSupplierId) {
        await updateSupplier.mutateAsync({ id: activeSupplierId, data: payload });
        closeModal();
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('failedToSaveSupplier'), 'error');
    }
  }, [activeSupplierId, closeModal, createSupplier, formData, modalMode, updateSupplier, t]);

  // Nút thùng rác = DEACTIVATE, không phải xoá (chốt với BE + PM 19/08/2026).
  //
  // DELETE thật là xoá mềm: supplier biến mất khỏi MỌI danh sách — kể cả tab
  // Inactive — và UI không có đường khôi phục (restore/ chỉ có ở API). Đó chính
  // là nguồn cơn bug #186: QA xoá 3 supplier rồi không hiểu vì sao tab Inactive
  // trống. Deactivate thì supplier còn thấy được ở tab Inactive, bật lại một
  // chạm qua form Edit, phiếu nhập cũ không ảnh hưởng.
  //
  // Luồng chuẩn BE chốt: PUT/PATCH kèm `is_active` (KHÔNG có endpoint
  // deactivate/ riêng cho suppliers — chỉ users mới có). Cần xoá thật để dọn
  // bản ghi tạo nhầm thì tạm thời gọi API trực tiếp.
  const handleConfirmDelete = useCallback(async () => {
    if (!activeSupplierId) return;
    try {
      await updateSupplier.mutateAsync({ id: activeSupplierId, data: { is_active: false } });
      closeModal();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('failedToDeactivateSupplier'), 'error');
    }
  }, [activeSupplierId, closeModal, updateSupplier, t]);

  const { triggerExport, isExporting } = useExport({ endpoint: '/suppliers/', filePrefix: 'suppliers' });


  return (
    <PageContainer>
      <PageHeader
        title={t('suppliers')}
        subtitle={t('manageSupplierRelationshipsAndConta')}
        actions={
          <div className="flex items-center gap-2">
            <ExportButton onExport={triggerExport} isLoading={isExporting} />
            <button
              onClick={handleAddSupplier}
              className="inline-flex items-center gap-2 rounded-lg bg-[#008CD2] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0076B1] transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('addSupplier')}
            </button>
          </div>
        }
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadSuppliers')}</p>
          <p className="mt-1 text-red-600">
            {error instanceof Error ? error.message : 'An error occurred while loading suppliers.'}
          </p>
        </div>
      )}

      <div className="mt-6">
        <SupplierKPICards stats={stats} isLoading={statsLoading} />
      </div>

      <div className="mt-6">
        <div className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative w-full min-w-0 flex-1 sm:w-auto sm:min-w-[200px] sm:max-w-xs">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <svg className="h-4 w-4 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => { setSearchValue(e.target.value); setCurrentPage(1); }}
                placeholder={t('searchByNameCodeOrPhone')}
                enterKeyHint="search"
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                className="w-full rounded-lg border border-[#E5E7EB] bg-white py-2 pl-9 pr-3 text-sm text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {/* Right dropdowns.

                Dropdown "Last 7/30/90 Days" đã GỠ: nó là đồ trang trí sót từ
                Figma — không value, không onChange, không nối vào query nào.
                Một ô lọc chọn gì danh sách cũng không đổi thì sớm muộn cũng
                thành bug report (đúng kịch bản bug #159 bên Stock Imports).
                `SupplierListParams` hiện không có param ngày; đã hỏi BE — có
                `created_at__gte/__lte` (hoặc tương đương) thì dựng lại và nối
                server-side như PurchaseOrdersPage. */}
            {/* `ml-auto` cố định (bug #238): trên mobile nó đẩy cụm dropdown sang
                phải trong khi hàng cha đã hết chỗ, nên "All Status" bị đùn ra
                ngoài mép khung lọc và không bấm được. Chỉ đẩy phải từ `sm` trở
                lên; dưới đó cụm này xuống dòng và chia đôi bề ngang. */}
            <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto sm:flex-nowrap sm:gap-3">
              {/* Mục #18 — nhãn nói ĐÚNG nghĩa: đây là "đã từng nhập hàng cho
                  cửa hàng", suy từ lịch sử phiếu nhập, KHÔNG phải "NCC thuộc
                  cửa hàng" (Supplier dùng chung toàn chuỗi, không có store_id). */}
              <div className="relative min-w-0 flex-1 sm:flex-none">
                <select
                  value={storeFilter}
                  onChange={(e) => { setStoreFilter(e.target.value); setCurrentPage(1); }}
                  className="w-full appearance-none truncate rounded-lg border border-[#E5E7EB] bg-white py-2 pl-3 pr-8 text-sm text-[#374151] focus:outline-none focus:ring-2 focus:ring-primary-500 sm:w-auto"
                  title={t('suppliersStoreFilterHint')}
                >
                  <option value="">{t('suppliersStoreFilterAll')}</option>
                  {storeOptions.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                  <svg className="h-4 w-4 text-[#6B7280]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <div className="relative min-w-0 flex-1 sm:flex-none">
                <select
                  value={activeTab}
                  onChange={(e) => { setActiveTab(e.target.value as StatusTab); setCurrentPage(1); }}
                  className="w-full appearance-none truncate rounded-lg border border-[#E5E7EB] bg-white py-2 pl-3 pr-8 text-sm text-[#374151] focus:outline-none focus:ring-2 focus:ring-primary-500 sm:w-auto"
                >
                  <option value="all">{t('allStatus')}</option>
                  <option value="active">{t('suppliersActive')}</option>
                  <option value="inactive">{t('suppliersInactive')}</option>
                </select>
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                  <svg className="h-4 w-4 text-[#6B7280]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mục #18: lọc theo cửa hàng mà ra rỗng là KẾT QUẢ ĐÚNG, không phải mất
          dữ liệu — cửa hàng chưa từng nhập hàng thì chưa có NCC nào. Nói thẳng
          ra, vì "không tìm thấy" chung chung sẽ bị hiểu là hỏng. */}
      {storeFilter && !isLoading && visibleCount === 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-sm font-semibold text-slate-700">{t('suppliersStoreFilterEmpty')}</p>
          <p className="mx-auto mt-2 max-w-xl text-xs text-slate-500">
            {t('suppliersStoreFilterEmptyHint')}
          </p>
        </div>
      )}

      <div className="mt-6">
        <SupplierTable
          data={visibleSuppliers}
          totalCount={visibleCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onView={(id) => openModal(id, 'view')}
          onEdit={(id) => openModal(id, 'edit')}
          onDelete={(id) => openModal(id, 'delete-confirm')}
          isLoading={isLoading}
        />
      </div>


      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6 sm:items-center" onClick={closeModal}>
          {/* `max-h` + `overflow` (bug #239): form NCC cao hơn màn hình laptop
              13" nên phần dưới — gồm cả nút Lưu — nằm ngoài viewport và không
              có gì cuộn tới được, vì hộp thoại trước đây không giới hạn chiều
              cao lẫn không cho cuộn. Header và footer đứng yên, chỉ ruột cuộn. */}
          <div
            className="flex max-h-[calc(100vh-3rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex shrink-0 items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {modalMode === 'delete-confirm' ? t('deactivateSupplier') : modalMode === 'edit' ? t('editSupplier') : t('addSupplier')}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {modalMode === 'delete-confirm' ? (
              <div className="px-6 pb-6">
                <p className="text-sm text-slate-600">{t('areYouSureDeleteSupplier')}</p>
                <div className="mt-6 flex justify-end gap-3">
                  <button onClick={closeModal} disabled={updateSupplier.isPending} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40">
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={updateSupplier.isPending}
                    className="rounded-lg bg-[#EF4444] px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60 transition-colors"
                  >
                    {updateSupplier.isPending ? t('processing') : t('deactivate')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
                {isLoadingDetail && modalMode === 'edit' && !supplierDetail ? (
                  <div className="py-8 text-center text-sm text-slate-500">{t('loading')}</div>
                ) : (
                  <div className="space-y-4">
                    {/* Supplier Name */}
                    <div>
                      <label className="mb-1 block text-sm text-slate-700">{t('supplierName')} <span className="text-red-500">*</span></label>
                      <input
                        ref={(el) => { fieldRefs.current['name'] = el; }}
                        value={formData.name}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        onBlur={() => handleFieldBlur('name')}
                        placeholder={t('enterSupplierName')}
                        className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {formErrors.name && formTouched.name && (
                        <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>
                      )}
                    </div>

                    {/* Tax Code */}
                    <div>
                      <label className="mb-1 block text-sm text-slate-700">{t('taxCode')}</label>
                      <input
                        value={formData.tax_code}
                        onChange={(e) => handleFieldChange('tax_code', e.target.value)}
                        placeholder={t('enterTaxCode')}
                        className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    {/* Contact Name + Contact Phone */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-sm text-slate-700">{t('contactName')}</label>
                        <input
                          value={formData.contact_name}
                          onChange={(e) => handleFieldChange('contact_name', e.target.value)}
                          placeholder={t('enterContactName')}
                          className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm text-slate-700">{t('contactPhone')}</label>
                        <input
                          ref={(el) => { fieldRefs.current['contact_phone'] = el; }}
                          value={formData.contact_phone}
                          onChange={(e) => handleFieldChange('contact_phone', e.target.value)}
                          onBlur={() => handleFieldBlur('contact_phone')}
                          placeholder={t('enterPhone')}
                          className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        {formErrors.contact_phone && formTouched.contact_phone && (
                          <p className="mt-1 text-xs text-red-600">{formErrors.contact_phone}</p>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="mb-1 block text-sm text-slate-700">{t('email')}</label>
                      <input
                        ref={(el) => { fieldRefs.current['email'] = el; }}
                        value={formData.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        onBlur={() => handleFieldBlur('email')}
                        placeholder="user@example.com"
                        className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {formErrors.email && formTouched.email && (
                        <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>
                      )}
                    </div>

                    {/* Lead Time Days */}
                    <div>
                      <label className="mb-1 block text-sm text-slate-700">{t('leadTimeDays')}</label>
                      <input
                        ref={(el) => { fieldRefs.current['lead_time_days'] = el; }}
                        type="number"
                        min={0}
                        value={formData.lead_time_days}
                        onChange={(e) => handleFieldChange('lead_time_days', e.target.value)}
                        onBlur={() => handleFieldBlur('lead_time_days')}
                        placeholder="0"
                        className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {formErrors.lead_time_days && formTouched.lead_time_days && (
                        <p className="mt-1 text-xs text-red-600">{formErrors.lead_time_days}</p>
                      )}
                    </div>

                    {/* Metadata */}
                    <div>
                      <label className="mb-1 block text-sm text-slate-700">{t('metadata')}</label>
                      <input
                        value={formData.metadata}
                        onChange={(e) => handleFieldChange('metadata', e.target.value)}
                        placeholder={t('enterMetadata')}
                        className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    {/* Status — bug #159 */}
                    <div>
                      <label className="mb-1 block text-sm text-slate-700">{t('status')}</label>
                      <select
                        value={formData.status}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: e.target.value === 'inactive' ? 'inactive' : 'active',
                          }))
                        }
                        className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="active">{t('suppliersActive')}</option>
                        <option value="inactive">{t('suppliersInactive')}</option>
                      </select>
                    </div>

                    {/* Logo NCC (sheet 2 mục #14) — chỉ sửa được ở chế độ
                        create/edit; màn xem giữ nguyên chỉ đọc */}
                    {modalMode !== 'view' && (
                      <ImageUploadField
                        label={t('supplierImage')}
                        value={formData.imageUrls}
                        onChange={(urls) =>
                          setFormData((prev) => ({ ...prev, imageUrls: urls }))
                        }
                        entityType="supplier"
                      />
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                      <button onClick={closeModal} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                        {t('cancel')}
                      </button>
                      <button
                        onClick={handleSubmitForm}
                        disabled={isSubmitting}
                        className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60 transition-colors"
                      >
                        {isSubmitting ? t('savingBtn') : t('saveBtn')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
}
